import React, { useEffect, useState } from 'react';
import { useResponsive } from '../utils/useResponsive';

const Header = ({ onBookVehicle, onMenuClick, onOpenSearch, currentPage }) => {
  const { isMobile, isTablet } = useResponsive();
  const [isIOS, setIsIOS] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    // Detect iOS devices
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Handle screen width changes
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // iPhone 16 series detection
  const isIPhone16Series = isIOS && (
    screenWidth <= 430 && // iPhone 16 Pro Max
    screenWidth >= 375    // iPhone 16 standard
  );
  
  const pageTitles = {
    overview: 'Dashboard',
    'garage-report': 'Garage Report',
    garagevehicles: 'Garage Vehicles',
    report: 'Report',
    calendar: 'Calendar',
    invoices: 'Invoices',
    archive: 'Archive',
    sales: 'Sales Board',
    'sales-report': 'Sales Report',
    links: 'Links',
    promotions: 'Promotions',
    'parts-lookup': 'Parts Lookup',
    finance: 'Finance',
    payment: 'Payment',
    'test-admin': 'Test Admin',
    'technician-dispatch': 'Technicians'
  };

  // Shorter titles for mobile so they fit in the header
  const mobileTitles = {
    overview: 'Dashboard',
    'garage-report': 'G. Report',
    garagevehicles: 'Vehicles',
    report: 'Report',
    calendar: 'Calendar',
    invoices: 'Invoices',
    archive: 'Archive',
    sales: 'Sales',
    'sales-report': 'S. Report',
    links: 'Links',
    promotions: 'Promos',
    'parts-lookup': 'Parts',
    finance: 'Finance',
    payment: 'Payment',
    'test-admin': 'Admin',
    'technician-dispatch': 'Tech'
  };

  const useShortTitle = isMobile || isTablet;
  const title = (useShortTitle ? mobileTitles[currentPage] : pageTitles[currentPage]) || pageTitles[currentPage] || 'Dashboard';

  // Shared button style so Search and Book Vehicle are identical on mobile
  const getButtonStyle = () => {
    const base = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isMobile ? '4px' : '8px',
      padding: isMobile ? '8px 12px' : '10px 16px',
      fontSize: isMobile ? '14px' : '16px',
      minHeight: isMobile ? '44px' : '40px',
      minWidth: isMobile ? '44px' : undefined,
      boxSizing: 'border-box',
      ...(isMobile && {
        borderRadius: '12px',
        whiteSpace: 'nowrap',
        fontWeight: '600',
      }),
    };
    if (isIPhone16Series) {
      return {
        ...base,
        padding: screenWidth <= 375 ? '8px 14px' : '10px 16px',
        fontSize: screenWidth <= 375 ? '14px' : '15px',
        borderRadius: '12px',
        minHeight: screenWidth <= 375 ? '44px' : '48px',
        minWidth: screenWidth <= 375 ? '44px' : '48px',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      };
    }
    return base;
  };

  const getMenuButtonStyle = () => {
    if (isIPhone16Series) {
      return {
        minHeight: screenWidth <= 375 ? '44px' : '48px',
        minWidth: screenWidth <= 375 ? '44px' : '48px',
        padding: screenWidth <= 375 ? '10px' : '12px',
        borderRadius: '12px',
        background: 'transparent',
        border: 'none',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        transition: 'all 0.2s ease'
      };
    }

    return {};
  };

  // Mobile: [Search | Book] | Title (center) | [Hamburger]
  // Desktop: [Hamburger | Title] | [Search | Book]
  const isMobileView = isMobile || isTablet;

  if (isMobileView) {
    return (
      <header className="main-header main-header-centered">
        <div className="main-header-left" style={{ display: 'flex', gap: isIPhone16Series ? '8px' : '12px', alignItems: 'center', flexShrink: 0 }}>
          {onOpenSearch && (
            <button
              type="button"
              className="hero-button hero-button-header-mobile"
              onClick={onOpenSearch}
              aria-label="Search (Ctrl+K)"
              style={{ ...getButtonStyle(), background: '#fdfcf9', border: '1px solid #e8e6e2', color: '#33475b' }}
              onTouchStart={() => {}}
            >
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <span style={{ marginLeft: '4px' }}>Search</span>
            </button>
          )}
          <button
            type="button"
            className="hero-button hero-primary hero-button-header-mobile"
            onClick={onBookVehicle}
            style={getButtonStyle()}
            aria-label="Book a vehicle"
            onTouchStart={() => {}}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span style={{ marginLeft: '4px' }}>Book</span>
          </button>
        </div>
        <h1 className="main-header-title" style={{ fontSize: isIPhone16Series ? (screenWidth <= 375 ? '16px' : '18px') : undefined, fontWeight: isIPhone16Series ? '700' : undefined, margin: 0, lineHeight: 1.2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </h1>
        <div className="main-header-right" style={{ flexShrink: 0 }}>
          <button type="button" className="mobile-menu-btn" onClick={onMenuClick} aria-label="Toggle menu" style={getMenuButtonStyle()} onTouchStart={() => {}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={isIPhone16Series ? '2.5' : '2'} style={{ width: screenWidth <= 375 ? '20px' : '24px', height: screenWidth <= 375 ? '20px' : '24px' }}>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </header>
    );
  }

  // Desktop: original order
  return (
    <header className="main-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0, flex: 1 }}>
        <button type="button" className="mobile-menu-btn" onClick={onMenuClick} aria-label="Toggle menu" style={getMenuButtonStyle()} onTouchStart={() => {}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '24px', height: '24px' }}>
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 600, margin: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h1>
      </div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
        {onOpenSearch && (
          <button type="button" className="hero-button" onClick={onOpenSearch} aria-label="Search (Ctrl+K)" style={{ ...getButtonStyle(), background: '#fdfcf9', border: '1px solid #e8e6e2', color: '#33475b' }} onTouchStart={() => {}}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <span style={{ marginLeft: '4px' }}>Search</span>
          </button>
        )}
        <button type="button" className="hero-button hero-primary" onClick={onBookVehicle} style={getButtonStyle()} aria-label="Book a vehicle" onTouchStart={() => {}}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span style={{ marginLeft: '4px' }}>Book Vehicle</span>
        </button>
      </div>
    </header>
  );
};

export default Header;

